import { DeleteOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { daysOfWeekEngOptions } from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.utils';
import SetNormalHoursForm from 'components/forms/location/hours/set-normal-hours-form/set-normal-hours-form.component';
import { useSetNormalHoursForm } from 'components/forms/location/hours/set-normal-hours-form/set-normal-hours-form.hooks';
import { useLocationHoursContext } from 'components/location-hours/location-hours.context';
import { SetHoursFormDataProps } from 'components/location-hours/location-hours.utils';
import { useTranslation } from 'react-i18next';
import FormDrawer, {
  FormDrawerProps,
} from '../../form-drawer/form-drawer.component';
import { UtilityDrawerHookReturnType } from '../../utility-drawer/utility-drawer.hooks';

type SetNormalHoursDrawerProps = {
  drawerData: SetHoursFormDataProps;
} & Omit<FormDrawerProps, 'children' | 'onClose' | 'formMethods' | 'onValid'> &
  UtilityDrawerHookReturnType;

const SetNormalHoursDrawer = ({
  title,
  open,
  close,
  drawerData,
  ...rest
}: SetNormalHoursDrawerProps) => {
  const { t } = useTranslation();
  const {
    normalHours: { removeDummyEvent, addEvents, normalHoursRef },
  } = useLocationHoursContext();
  const { form } = useSetNormalHoursForm(drawerData);

  const finalOnValid = data => {
    try {
      removeDummyEvent();
      addEvents({
        ...data,
        daysOfWeek:
          data.daysOfWeek[0].id === 'ALL'
            ? daysOfWeekEngOptions()
            : daysOfWeekEngOptions().filter(enItem =>
                data.daysOfWeek.some(item => item.id === enItem.id),
              ),
      });

      form.reset();
      close();
    } catch (_e) {}
  };

  const cancel = () => {
    removeDummyEvent();

    form.reset();
    form.clearErrors();
    close();
  };

  return (
    <FormDrawer
      title={t('drawer.setNormalHoursDrawer.title')}
      data-testid="SetNormalHoursDrawer"
      open={open}
      onValid={finalOnValid}
      onClose={cancel}
      cancel={cancel}
      formMethods={form}
      buttons={(isSubmitting, isValidating) => {
        return (
          <>
            <LoadingButton
              type="submit"
              fullWidth
              loading={isSubmitting || isValidating}
              data-testid="FormDrawer.Button.Submit"
            >
              {t('drawer.setNormalHoursDrawer.buttons.apply')}
            </LoadingButton>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                onClick={cancel}
                variant="outlined"
                data-testid="FormDrawer.Button.Cancel"
                disabled={isSubmitting || isValidating}
              >
                {t('forms.buttons.cancel')}
              </Button>
              {drawerData?.eventId && (
                <Button
                  color="error"
                  onClick={() => {
                    normalHoursRef.current
                      .getApi()
                      .getEventById(drawerData?.eventId)
                      .remove();
                    close();
                  }}
                  data-testid="FormDrawer.Button.Delete"
                  disabled={isSubmitting || isValidating}
                  sx={{ minWidth: 'auto' }}
                  variant="outlined"
                >
                  <DeleteOutlined />
                </Button>
              )}
            </Stack>
          </>
        );
      }}
      {...rest}
    >
      {open && <SetNormalHoursForm eventId={drawerData?.eventId} />}
    </FormDrawer>
  );
};

export default SetNormalHoursDrawer;
